import MemoizedIcon from '@components/MemoizedIcon';
import useMediaQuery from '@hooks/useMediaQuery';
import userIcon from '@iconify/icons-heroicons-solid/user';
import { IconifyIcon } from '@iconify/react';
import { MAX_WIDTH_SM } from '@src/constants';
import React, { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { getAvatarInitial } from './utils';

// ts-prune-ignore-next used in test
export const SIZE_MAP = [
  { wrapper: 80, icon: 62, fontSize: 'text-3xl' }, // size: 1
  { wrapper: 72, icon: 52, fontSize: 'text-3xl' }, // size: 2
  { wrapper: 64, icon: 44, fontSize: 'text-3xl' }, // size: 3
  { wrapper: 56, icon: 36, fontSize: 'text-2xl' }, // size: 4
  { wrapper: 48, icon: 28, fontSize: 'text-xl' }, // size: 5
  { wrapper: 40, icon: 24, fontSize: 'text-lg' }, // size: 6
  { wrapper: 32, icon: 20, fontSize: 'text-base' }, // size: 7
  { wrapper: 24, icon: 16, fontSize: 'text-xs' }, // size: 8
  { wrapper: 16, icon: 8, fontSize: 'text-[8px]' }, // size: 9
];
// ts-prune-ignore-next used in test
export type Size = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

interface AvatarProps {
  size?: Size;
  wrapperClassName?: string;
  wrapperSize?: number;
  iconClassName?: string;
  iconSize?: number;
  name?: string | null;
  customIcon?: IconifyIcon;
}

const Avatar: FC<AvatarProps> = ({
  size,
  wrapperClassName,
  iconClassName,
  wrapperSize,
  iconSize,
  name,
  customIcon,
}) => {
  const isMobile = useMediaQuery(MAX_WIDTH_SM);
  const fallbackSize = isMobile ? 6 : 1;
  const sizeToUse = size ?? fallbackSize;
  const { wrapper, icon, fontSize } = SIZE_MAP[sizeToUse - 1];

  return (
    <div
      data-testid="avatar-wrapper"
      style={{
        height: wrapperSize ?? wrapper,
        width: wrapperSize ?? wrapper,
      }}
      className={twMerge(
        'flex flex-none items-center justify-center rounded-full',
        name ? 'bg-secondaryGreen-300' : 'bg-neutral-40',
        wrapperClassName,
      )}
    >
      {name ? (
        <p className={twMerge('font-semibold text-white', fontSize)}>
          {getAvatarInitial(name)}
        </p>
      ) : (
        <MemoizedIcon
          icon={customIcon ?? userIcon}
          className={twMerge('text-neutral-60', iconClassName)}
          width={iconSize ?? icon}
          height={iconSize ?? icon}
        />
      )}
    </div>
  );
};

export default Avatar;
